import { css } from "styled-components/macro";
import {
  useIsAdmin,
  useEffectOnce,
  useFocusRange,
} from "utils/customHooks/genericHooks";
import { useIsMidgardDDAEnabled } from "components/Dashboard/GroupMetrics/useMetrics";
import { ONE_DAY_OF_MS } from "utils/timeUtils";
import { Close, Warning } from "@mui/icons-material";
import { NotificationCard } from "components/SimpleComponents/TopBanner/NotificationCard";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { useTraceToHoneycomb } from "tracer";
import { useAuth0 } from "contexts/AuthManager/Auth";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { IconButton } from "@mui/material";
import { useState } from "react";

export function TrendsPageMisconfiguredWarning() {
  const [dismissed, setDismissed] = useState(false);
  const focusRange = useFocusRange();
  const [
    {
      general: { overrideDdaLimitDays },
    },
  ] = useFlightConfig();
  const isMidgardDDAEnabled = useIsMidgardDDAEnabled();
  const trendsDateRangeDays = focusRange
    ? Math.ceil(focusRange.lt - focusRange.ge) / ONE_DAY_OF_MS
    : 0;
  const limit = overrideDdaLimitDays || 1;
  const isAboveMidgardDDALimit = trendsDateRangeDays > limit;
  const showWarning = isMidgardDDAEnabled && isAboveMidgardDDALimit;
  const isAdminUser = useIsAdmin();

  // trace to honeycomb when this occurs, so we can e.g. set up a slack alert
  const traceToHoneycomb = useTraceToHoneycomb({
    sampleRateOverride: 1,
  });
  const ckey = useCustomerKey();
  const { user } = useAuth0();
  useEffectOnce({
    callback: () => {
      console.log("✍ tracing to honeycomb - Trends Page Misconfigured");
      traceToHoneycomb({
        dataToTrace: {
          // span name
          name: "Trends Page Misconfigured",
          // additional data ("tags")
          customer: String(ckey),
          email: String(user?.email),
          url: `${window.location.origin}${window.location.pathname}${window.location.search}${window.location.hash}`,
          trendsDateRangeDays,
          overrideDdaLimitDays,
        },
      });
    },
    shouldRun: showWarning,
    dependencies: [showWarning],
  });

  return showWarning && !dismissed ? (
    <NotificationCard
      message={
        <div>
          <div>
            {`Trends page is misconfigured — requested date range (${trendsDateRangeDays} day${
              trendsDateRangeDays === 1 ? "" : "s"
            }) is larger than the current limit (${limit} day${
              limit === 1 ? "" : "s"
            }).`}
          </div>
          <div>
            {isAdminUser ? (
              <>
                Either{" "}
                <a
                  href="https://app.launchdarkly.com/default/production/features/flight-override-is-midgard-enabled/targeting"
                  target="_blank"
                  rel="noreferrer"
                >
                  disable Midgard
                </a>{" "}
                or{" "}
                <a
                  href={`https://${ckey}.gke-us-east1.raven-cloud.com/midgard/admin/deployment_config/flightconfig/`}
                  target="_blank"
                  rel="noreferrer"
                >
                  increase the limit
                </a>
                .
              </>
            ) : (
              <>
                We have been notified and are working on it. If the issue
                persists,{" "}
                <a
                  href="https://raven.ai/contact-us/"
                  target="_blank"
                  rel="noreferrer"
                >
                  contact support
                </a>
                .
              </>
            )}
          </div>
          <IconButton
            onClick={() => setDismissed(true)}
            style={{
              position: "absolute",
              right: 0,
              top: 0,
            }}
          >
            <Close />
          </IconButton>
        </div>
      }
      icon={<Warning style={{ opacity: 0.6, width: 30, height: 30 }} />}
      variant={"warning"}
      customCss={css`
        margin: 12px 12px 0;
      `}
    />
  ) : null;
}
